import { useState } from 'react';
import type { AppSession, MeView, PatientView, TrackActionId } from '../../lib/types';
import type { AuthConfig } from '../../lib/home';
import { ApiError, api } from '../../lib/api';
import { fmtBp, fmtClock, fmtCoins, fmtInt, greeting, localHour, pct } from '../../lib/format';
import { firstName } from '../../lib/session';
import { Icon } from '../../components/HeartMark';
import { Card, Progress } from '../../components/Bits';

/** How each track action looks and where it goes. The track decides which of these a patient sees. */
const ACTION: Record<TrackActionId, { href: string; icon: Parameters<typeof Icon>[0]['name']; tone: string }> = {
  'log-bp': { href: '#/bp/log', icon: 'plus', tone: 'red' },
  'log-weight': { href: '#/log/weight', icon: 'trend', tone: 'blue' },
  'log-pulse': { href: '#/log/pulse', icon: 'health', tone: 'purple' },
  'log-steps': { href: '#/log/steps', icon: 'steps', tone: 'green' },
  symptoms: { href: '#/symptoms', icon: 'alert', tone: 'gold' },
  coach: { href: '#/coach', icon: 'chat', tone: 'green' },
  'call-clinic': { href: '#/profile', icon: 'phone', tone: 'blue' },
};

/**
 * The dashboard. A greeting banner across the top, then two columns: the week and today's
 * medications on the left, the numbers, coins and quick actions on the right.
 */
export function HomeTab({ session, view, reload, say }: { session: AppSession; me: MeView; config: AuthConfig | null; view: PatientView; reload: () => Promise<void>; say: (s: string) => void; onSignOut: () => void }) {
  const [busyDose, setBusyDose] = useState<string | null>(null);
  const hour = localHour(view.now, view.profile.tzOffsetMinutes);
  const p = view.progress;
  const taken = view.today.filter((d) => d.taken).length;
  // A goal the app tracks has to be one the patient can fill in. Some plans carry a step goal that
  // their track would not have chosen, and before this there was no way to log a step at all.
  const actions = [...view.track.quickActions];
  if (view.goals.some((g) => g.kind === 'steps') && !actions.some((a) => a.id === 'log-steps')) {
    actions.splice(1, 0, { id: 'log-steps', label: 'Log steps' });
  }
  const scheduleOf = (medicationId: string) => (view.medications.find((m) => m.id === medicationId)?.times.length ?? 1) > 1 ? 'Twice daily' : 'Once daily';

  /**
   * Tick a dose, or un-tick one ticked by mistake. A checkbox that cannot be cleared is a trap: the
   * only way out of a misclick used to be leaving it wrong, and a wrong adherence number is worse
   * than an empty one for the person reading it at the clinic.
   */
  const toggleDose = async (medicationId: string, slot: number, taken: boolean) => {
    const key = `${medicationId}:${slot}`;
    setBusyDose(key);
    try {
      const r = await api.postEvent({ type: taken ? 'untake-med' : 'take-med', medicationId, slot }, session.token);
      if (r.earned.length > 0) say(`+${r.earned.reduce((n, e) => n + e.coins, 0)} HeartCoins — every dose taken today!`);
      await reload();
    } catch (e) {
      say(e instanceof ApiError ? e.message : `Could not ${taken ? 'un-tick' : 'record'} that dose.`);
    } finally {
      setBusyDose(null);
    }
  };

  return (
    <>
      <section className="banner">
        <div className="banner-main">
          <h1>
            {greeting(hour)}, {firstName(view.profile.name)} 💪
          </h1>
          <p className="sub">let's keep your heart strong</p>
        </div>
        {view.streak.current > 0 ? (
          <span className="streak streak-lg">
            <span aria-hidden="true">🔥</span> {view.streak.current} day streak
          </span>
        ) : null}
        <a className="insight-strip" href="#/coach">
          <span className="bubble bubble-white bubble-sm">
            <Icon name="chat" size={18} />
          </span>
          <span className="row-main">
            <strong>Your AI Coach has insights</strong>
            <span>{view.insight ? `“${view.insight}”` : 'Check in with your coach to start today.'}</span>
          </span>
          <span className="chev" aria-hidden="true">
            ›
          </span>
        </a>
      </section>

      <div className="grid-2">
        <div className="stack">
          <Card>
            <div className="card-head">
              <h2>This Week's Progress</h2>
              <Icon name="star" size={18} />
            </div>
            <div className="goal">
              <div className="goal-row">
                <span>Blood Pressure Goal</span>
                <span className={p.bp.onTrack ? 'good' : 'warn'}>{p.bp.onTrack ? 'On Track' : 'Behind'}</span>
              </div>
              <Progress value={p.bp.done} max={p.bp.target} />
              <span className="hint">
                {p.bp.done}/{p.bp.target} readings
              </span>
            </div>
            <div className="goal">
              <div className="goal-row">
                <span>Daily Steps</span>
                <span className={p.steps.daysComplete >= 7 ? 'good' : ''}>{p.steps.daysComplete >= 7 ? 'Complete ✓' : `${p.steps.daysComplete}/7 days`}</span>
              </div>
              <Progress value={p.steps.daysComplete} max={7} tone="green" />
              <span className="hint">{p.steps.daysComplete >= 7 ? 'Goal completed! 7/7 days' : `Goal hit ${p.steps.daysComplete} of 7 days`}</span>
            </div>
            <div className="goal">
              <div className="goal-row">
                <span>Medication Adherence</span>
                <span className={p.adherence.percent >= 95 ? 'good' : 'warn'}>{pct(p.adherence.percent)}</span>
              </div>
              <Progress value={p.adherence.percent} />
              <span className="hint">{p.adherence.daysComplete}/7 days completed</span>
            </div>
          </Card>

          <Card>
            <div className="card-head">
              <h2>Today's Medications</h2>
              <span className="hint">
                {taken}/{view.today.length} taken
              </span>
            </div>
            {view.today.length === 0 ? <p className="hint">No medications on your plan yet.</p> : null}
            {view.today.map((d) => {
              const key = `${d.medicationId}:${d.slot}`;
              return (
                <button
                  key={key}
                  type="button"
                  className={`dose ${d.taken ? 'taken' : ''}`}
                  disabled={busyDose === key}
                  onClick={() => void toggleDose(d.medicationId, d.slot, d.taken)}
                  aria-pressed={d.taken}
                  title={d.taken ? `Un-tick ${d.name} — if you ticked it by mistake` : `Mark ${d.name} taken`}
                >
                  <span className={`tick ${d.taken ? 'on' : ''}`}>{d.taken ? <Icon name="check" size={14} /> : null}</span>
                  <span className="row-main">
                    <span className={`row-title ${d.taken ? 'struck' : ''}`}>{d.name}</span>
                    <span className="row-sub">
                      {scheduleOf(d.medicationId)} · {fmtClock(d.time)}
                    </span>
                  </span>
                  <span className={`badge ${d.taken ? 'badge-undo' : ''}`}>{d.taken ? 'Undo' : 'Mark taken'}</span>
                </button>
              );
            })}
          </Card>
        </div>

        <div className="stack">
          <div className="tile-row">
            <div className="tile">
              <span className="bubble bubble-red bubble-sm tile-icon">
                <Icon name="star" size={17} />
              </span>
              <strong className="tile-value">{fmtBp(view.latestReading)}</strong>
              <span className="tile-label">Blood Pressure</span>
              {view.systolicChange !== null ? (
                <span className={view.systolicChange <= 0 ? 'good small' : 'warn small'}>
                  {view.systolicChange <= 0 ? '↓' : '↑'} {Math.abs(view.systolicChange)} points
                </span>
              ) : (
                <span className="hint small">vs last week</span>
              )}
            </div>
            <div className="tile">
              <span className="bubble bubble-green bubble-sm tile-icon">
                <Icon name="steps" size={17} />
              </span>
              <strong className="tile-value">{fmtInt(view.stepsToday)}</strong>
              <span className="tile-label">Steps Today</span>
              <span className="good small">Goal: {p.steps.todayGoal ? fmtInt(p.steps.todayGoal) : '—'}</span>
            </div>
          </div>

          <a className="coins-banner" href="#/rewards">
            <span className="coin" aria-hidden="true">
              <Icon name="coin" size={24} />
            </span>
            <span className="row-main">
              <strong>{fmtCoins(view.coins.balance)} HeartCoins</strong>
              <span>+{fmtCoins(view.coins.earnedThisWeek)} earned this week · Tap to redeem</span>
            </span>
            <span className="chev" aria-hidden="true">
              ›
            </span>
          </a>

          <Card>
            <h2>Quick Actions</h2>
            <div className="quick-grid">
              {actions.map((a) => {
                const look = ACTION[a.id];
                return (
                  <a key={a.id} href={look.href} className="quick-item">
                    <span className={`bubble bubble-${look.tone} bubble-sm`}>
                      <Icon name={look.icon} size={18} />
                    </span>
                    {a.label}
                  </a>
                );
              })}
              <button type="button" className="quick-item" onClick={() => say(`Call ${view.practiceName} on the number on your appointment card. HeartCoach does not place calls.`)}>
                <span className="bubble bubble-blue bubble-sm">
                  <Icon name="phone" size={18} />
                </span>
                Call Clinic
              </button>
            </div>
          </Card>

          {view.inboxUnread > 0 ? (
            <a className="card row-link" href="#/inbox" style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
              <span className="bubble bubble-gold bubble-sm">
                <Icon name="bell" size={17} />
              </span>
              <span className="row-main">
                <strong>{view.inboxUnread} new from your care team</strong>
                <span className="hint">Open your messages</span>
              </span>
              <span className="chev" aria-hidden="true">
                ›
              </span>
            </a>
          ) : null}
        </div>
      </div>
    </>
  );
}
